import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import {
  createRootRoute,
  type ErrorComponentProps,
  Link,
  Outlet,
  useNavigate,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { isHTTPError } from "ky";
import { Compass, LifeBuoy, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { ConfirmProvider } from "@/components/confirm-dialog";
import { PageShell } from "@/components/page-shell";
import { Button } from "@/components/ui/button";
import { organizationQueryOptions } from "@/lib/data";
import { applyEmbedDocumentFlags, isEmbedRoute } from "@/lib/embed";
import { OrganizationProvider } from "@/lib/organization";
import { isNetworkFailure } from "@/lib/server-errors";
import { isSignedIn, onSessionEnded } from "@/lib/session";
import { cn } from "@/lib/utils";

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundPage,
  errorComponent: RouteErrorPage,
});

function TryAgainButton({
  busy = false,
  onClick,
}: {
  busy?: boolean;
  onClick: () => void;
}) {
  return (
    <Button type="button" className="mt-4" onClick={onClick} disabled={busy}>
      {busy ? (
        <>
          <Loader2 className="size-4 animate-spin" />
          Trying again...
        </>
      ) : (
        "Try again"
      )}
    </Button>
  );
}

function RouteErrorPage({ error }: ErrorComponentProps) {
  const router = useRouter();
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (!isNetworkFailure(error)) Sentry.captureException(error);
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-sm text-center">
        <h1 className="font-heading text-2xl font-semibold text-foreground">
          Something went wrong
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          We hit an unexpected error loading this page. Please try again.
        </p>
        <TryAgainButton
          busy={retrying}
          onClick={() => {
            setRetrying(true);
            // Re-runs the loaders and resets this boundary; reset() would do only the latter.
            void router.invalidate().finally(() => setRetrying(false));
          }}
        />
      </div>
    </div>
  );
}

function NotFoundPage() {
  const homePath = isSignedIn() ? "/steps" : "/";

  return (
    <PageShell>
      <div className="flex flex-col items-center rounded-2xl border border-border bg-gradient-to-br from-brand-tint via-card to-card px-6 py-12 text-center shadow-[var(--shadow-card)]">
        <div className="flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Compass className="size-7" />
        </div>
        <p className="mt-5 font-mono text-xs tracking-widest text-muted-foreground uppercase">
          Error 404
        </p>
        <h1 className="mt-2 font-heading text-2xl font-semibold text-foreground">
          We can't find that page
        </h1>
        <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
          The link may be broken or the page may have moved. Let's get you back
          on track.
        </p>
        <div className="mt-6 flex w-full max-w-xs flex-col gap-2">
          <Button asChild size="lg">
            <Link to={homePath}>
              Go to {isSignedIn() ? "your steps" : "sign in"}
            </Link>
          </Button>
          <Button asChild variant="ghost" size="sm" data-icon="inline-start">
            <Link to="/help">
              <LifeBuoy className="size-3.5" />
              Get help
            </Link>
          </Button>
        </div>
      </div>
    </PageShell>
  );
}

function NavigationProgress() {
  const isLoading = useRouterState({ select: (s) => s.isLoading });

  if (!isLoading) return null;

  return (
    <div className="fixed inset-x-0 top-0 z-50 h-0.5">
      <div className="h-full animate-progress bg-primary" />
    </div>
  );
}

function RootComponent() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Not the location: that changes when a navigation starts, while the old page is
  // still up. The deepest match is committed in the same render as its content, so
  // the key flips exactly when the new page first appears. resolvedLocation is set
  // in a later batch and is undefined on the first render. A global not-found
  // renders only the root match, which carries no trace of the URL (pathname "/",
  // empty params, same id). The index route's match also has pathname "/", so
  // moving between unknown URLs, or between "/" and an unknown URL, keeps this
  // key and doesn't replay the entrance animation. The location does tell them
  // apart, but it moves before the new page commits; resolvedLocation moves
  // after it.
  const renderedPathname = useRouterState({
    select: (s) => s.matches.at(-1)?.pathname,
  });
  const {
    data: organization,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(organizationQueryOptions);

  // The sign-up route is iframed into dealer websites, so every state the root can
  // render — including these bootstrap ones — has to stay chrome-free and sized to
  // its content. main.tsx flags the document before first paint for the real iframe
  // load; this keeps it right across navigation and under the tests' memory history.
  const isEmbed = isEmbedRoute(pathname);
  useEffect(() => {
    applyEmbedDocumentFlags(pathname);
  }, [pathname]);

  useEffect(() => onSessionEnded(() => void navigate({ to: "/" })), [navigate]);

  if (isLoading) {
    return (
      <div
        className={cn(
          "flex items-center justify-center",
          isEmbed ? "p-6" : "min-h-screen bg-background",
        )}
      >
        <div className="flex items-center gap-2.5 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          <span className="text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  if (isError || !organization) {
    // Only a 404 means there's no portal here. Anything else is most often a phone
    // losing signal, and the Applicant shouldn't be told the portal doesn't exist.
    const notFound = isHTTPError(error) && error.response.status === 404;
    return (
      <div
        className={cn(
          "flex items-center justify-center px-4",
          isEmbed ? "py-6" : "min-h-screen bg-background",
        )}
      >
        <div className="max-w-sm text-center">
          <h1 className="font-heading text-2xl font-semibold text-foreground">
            {notFound ? "Portal not found" : "Something went wrong"}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {notFound
              ? "Unable to load the application portal. Please check the URL and try again."
              : "We couldn't load this right now. Check your connection and try again."}
          </p>
          {/* A retry with no data yet goes back to isLoading, whose spinner shows it's under way. */}
          <TryAgainButton onClick={() => void refetch()} />
        </div>
      </div>
    );
  }

  return (
    <OrganizationProvider organization={organization}>
      <ConfirmProvider>
        {!isEmbed && <NavigationProgress />}
        {/* The entrance animation moves the content, which would have the embed
            renegotiating its iframe height for the whole 200ms. Keyed on the
            rendered page so only the arriving page remounts and animates, not
            the one being left while the next loader runs. */}
        <div
          key={renderedPathname}
          className={cn(
            !isEmbed &&
              "animate-in duration-200 ease-out fade-in slide-in-from-bottom-1",
          )}
        >
          <Outlet />
        </div>
      </ConfirmProvider>
    </OrganizationProvider>
  );
}
